import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { trackApiCall, sentryLogger } from '../utils/sentry';

// Extend Axios config type to include our metadata
interface RequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    tracker: ReturnType<typeof trackApiCall>;
  };
}

// Base URL for API requests originating from axios-based services
const API_URL = import.meta.env.VITE_API_URL || 'https://api.skillfoundry.dev';

// Ensure HTTPS is used for API URLs
const ensureHttps = (url: string): string => {
  if (!url) return url;
  // If it's a relative URL, return as is
  if (url.startsWith('/')) return url;
  // If it's already HTTPS, return as is
  if (url.startsWith('https://')) return url;
  // If it's HTTP, convert to HTTPS
  if (url.startsWith('http://')) {
    return `https://${url.substring(7)}`;
  }
  // If it's a domain without protocol, add HTTPS
  return `https://${url}`;
};

// Helper to extract the JWT from Descope session storage
const getAuthToken = (): string | null => {
  const readToken = (raw: string | null) => {
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data?.token || null;
    } catch {
      return null;
    }
  };

  // 1️⃣ Session storage (preferred by our dev bypass)
  let token = readToken(sessionStorage.getItem('descope.session'));

  // 2️⃣ Local storage – Descope SDK persists here in production
  if (!token) {
    token = readToken(localStorage.getItem('descope.session'));
  }

  // Check other possible token locations if we still don't have one
  if (!token) {
    // Try window global
    if ((window as any).__DESCOPE_JWT__) {
      token = (window as any).__DESCOPE_JWT__;
    }
    // Try any available header in case it was set directly
    else if (axios.defaults.headers.common['Authorization']) {
      const authHeader = axios.defaults.headers.common['Authorization'] as string;
      if (authHeader?.startsWith('Bearer ')) {
        token = authHeader.substring(7);
      }
    }
  }

  return token;
};

// Create axios instance with HTTPS enforced
const httpClient = axios.create({
  baseURL: ensureHttps(API_URL),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach interceptor to inject Authorization header and ensure HTTPS
httpClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    // Do not overwrite if already set explicitly
    if (!config.headers['Authorization'] && !config.headers['authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Ensure HTTPS for absolute URLs
  if (config.url && !config.url.startsWith('/')) {
    config.url = ensureHttps(config.url);
  }
  if (config.baseURL) {
    config.baseURL = ensureHttps(config.baseURL);
  }

  // Force HTTPS for all requests
  if (config.url?.startsWith('http://')) {
    throw new Error('HTTP requests are not allowed. Use HTTPS instead.');
  }
  if (config.baseURL?.startsWith('http://')) {
    throw new Error('HTTP base URLs are not allowed. Use HTTPS instead.');
  }

  return config;
});

// Add request interceptor for tracking API calls
httpClient.interceptors.request.use(
  (config: RequestConfig) => {
    const method = config.method?.toUpperCase() || 'GET';
    const url = config.url || 'unknown';
    const fullUrl = url.startsWith('/') ? `${config.baseURL}${url}` : url;
    
    // Start tracking the API call
    const tracker = trackApiCall(url, method);
    
    // Store the tracker in the config for use in the response interceptor
    config.metadata = { tracker };
    
    // Log the API request start
    sentryLogger.debug("HTTP request initiated", {
      method,
      url,
      fullUrl,
      hasAuth: !!config.headers?.Authorization,
      contentType: config.headers?.['Content-Type'] || 'application/json',
      dataSize: config.data ? JSON.stringify(config.data).length : 0
    });
    
    return config;
  },
  (error: AxiosError) => {
    sentryLogger.error("HTTP request configuration failed", {
      error: error.message,
      code: error.code,
      url: error.config?.url
    });
    
    return Promise.reject(error);
  }
);

// Add response interceptor for completing API call tracking
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Complete tracking with success status
    const config = response.config as RequestConfig;
    const method = config.method?.toUpperCase() || 'GET';
    const url = config.url || 'unknown';
    
    config.metadata?.tracker?.finish(response.status);
    
    // Log successful response
    sentryLogger.logApiCall(
      method,
      url,
      response.status,
      undefined, // duration will be calculated by tracker
      undefined  // no error
    );
    
    sentryLogger.debug("HTTP response received", {
      method,
      url,
      status: response.status,
      statusText: response.statusText,
      responseSize: response.data ? JSON.stringify(response.data).length : 0,
      contentType: response.headers['content-type']
    });
    
    return response;
  },
  (error: AxiosError) => {
    // Complete tracking with error status and details
    const config = error.config as RequestConfig;
    const method = config?.method?.toUpperCase() || 'GET';
    const url = config?.url || 'unknown';
    const status = error.response?.status;
    
    config?.metadata?.tracker?.finish(status, error);
    
    // Log failed response
    sentryLogger.logApiCall(
      method,
      url,
      status,
      undefined, // duration will be calculated by tracker
      error
    );
    
    sentryLogger.error("HTTP request failed", {
      method,
      url,
      status: status || 'no_response',
      statusText: error.response?.statusText || 'unknown',
      errorMessage: error.message,
      errorCode: error.code,
      responseData: error.response?.data,
      isNetworkError: !error.response,
      isTimeoutError: error.code === 'ECONNABORTED'
    });
    
    return Promise.reject(error);
  }
);

export { httpClient }; 